/* ================================================================
   Image Compo — Web MIDI + action registry + LED feedback
   Ondersteunt APC Mini (mk1/mk2) + AKAI LPD8.
================================================================ */

/* --------- ACTIONS ---------
   Steady-active support voor LED feedback via evalActive:
   - view.<X>          → state.current_view === X
   - phase.<X>         → show.phase === X
   - entry.goto.<N>    → state.current_entry_id === approved[N-1].id
   - reveal.step.<N>   → state.reveal_step === N
   - public_reveal.step.<N> → state.public_reveal_step === N
   Andere acties (transient triggers) blijven op idle-kleur staan.
================================================================ */
const _viewActions   = ['idle','entry','pauze','reveal','public_reveal','results']
  .map(v => ({ key: `view.${v}`, label: `View: ${v}`, group: 'View' }))
const _entryNav      = [
  { key: 'entry.next', label: 'Entry: Volgende', group: 'Entry' },
  { key: 'entry.prev', label: 'Entry: Vorige',   group: 'Entry' },
]
const _entryGoto     = Array.from({ length: 12 }, (_, i) => ({
  key: `entry.goto.${i + 1}`, label: `Entry: Direct #${i + 1}`, group: 'Entry direct'
}))
const _revealNav     = [
  { key: 'reveal.next',  label: 'Reveal: Volgende punten', group: 'Reveal (admin)' },
  { key: 'reveal.prev',  label: 'Reveal: Terug',            group: 'Reveal (admin)' },
  { key: 'reveal.reset', label: 'Reveal: Reset',            group: 'Reveal (admin)' },
]
const _revealSteps   = Array.from({ length: 12 }, (_, i) => ({
  key: `reveal.step.${i}`, label: `Reveal: Stap ${i}`, group: 'Reveal stap'
}))
const _publicRevealNav = [
  { key: 'public_reveal.next',  label: 'Publiek reveal: Volgende', group: 'Publiek reveal' },
  { key: 'public_reveal.prev',  label: 'Publiek reveal: Terug',    group: 'Publiek reveal' },
  { key: 'public_reveal.reset', label: 'Publiek reveal: Reset',    group: 'Publiek reveal' },
]
const _publicRevealSteps = Array.from({ length: 12 }, (_, i) => ({
  key: `public_reveal.step.${i}`, label: `Publiek reveal: Stap ${i}`, group: 'Publiek reveal stap'
}))
const _phaseActions  = ['draft','submissions','live','pauze','reveal','done']
  .map(p => ({ key: `phase.${p}`, label: `Phase: ${p}`, group: 'Phase' }))
const _phaseNav      = [
  { key: 'phase.next', label: 'Phase: Volgende',  group: 'Phase' },
  { key: 'phase.prev', label: 'Phase: Vorige',    group: 'Phase' },
]
const _showToggles   = [
  { key: 'submissions.toggle', label: 'Inschrijvingen aan/uit', group: 'Show' },
  { key: 'emergency.blackout', label: 'Emergency blackout',     group: 'Show' },
]

export const ACTIONS = [
  ..._viewActions,
  ..._entryNav, ..._entryGoto,
  ..._revealNav, ..._revealSteps,
  ..._publicRevealNav, ..._publicRevealSteps,
  ..._phaseActions, ..._phaseNav,
  ..._showToggles,
]
export const ACTION_GROUPS = [
  'View', 'Entry', 'Entry direct',
  'Reveal (admin)', 'Reveal stap',
  'Publiek reveal', 'Publiek reveal stap',
  'Phase', 'Show',
]
export const PHASE_ORDER = ['draft','submissions','live','pauze','reveal','done']

/* --------- Web MIDI --------- */
export async function openMIDI() {
  if (!navigator.requestMIDIAccess) throw new Error('Web MIDI API niet beschikbaar (Chrome/Edge vereist)')
  return navigator.requestMIDIAccess({ sysex: false })
}
export function listInputs(access)  { return Array.from(access.inputs.values()) }
export function listOutputs(access) { return Array.from(access.outputs.values()) }

export function attachInputListener(input, onEvent) {
  const handler = (msg) => {
    const [status, data1 = 0, data2 = 0] = msg.data
    const type = status & 0xF0
    const channel = status & 0x0F
    if (type === 0x80) return
    if (type === 0x90 && data2 === 0) return
    onEvent({ type, channel, data1, data2, raw: Array.from(msg.data) })
  }
  input.onmidimessage = handler
  return () => { input.onmidimessage = null }
}

export function eventKey(evt) {
  if (!evt) return null
  const t = evt.type
  if (t === 0x90) return `note:${evt.channel}:${evt.data1}`
  if (t === 0xB0) return `cc:${evt.channel}:${evt.data1}`
  if (t === 0xC0) return `pc:${evt.channel}:${evt.data1}`
  return null
}
export function eventLabel(evt) {
  if (!evt) return ''
  const t = evt.type
  if (t === 0x90) return `NOTE ${evt.data1} · ch${evt.channel + 1} · vel ${evt.data2}`
  if (t === 0xB0) return `CC ${evt.data1} · ch${evt.channel + 1} · val ${evt.data2}`
  if (t === 0xC0) return `PC ${evt.data1} · ch${evt.channel + 1}`
  return `0x${t.toString(16)} ${evt.data1} ${evt.data2}`
}
export function keyLabel(key) {
  if (!key) return ''
  const [kind, ch, num] = key.split(':')
  return `${kind.toUpperCase()} ${num} · ch${Number(ch) + 1}`
}
export function keyIsNote(key) { return typeof key === 'string' && key.startsWith('note:') }
export function keyIsCC(key)   { return typeof key === 'string' && key.startsWith('cc:') }
export function keyToNoteNum(key) {
  const m = /^note:(\d+):(\d+)$/.exec(key || '')
  return m ? { channel: Number(m[1]), num: Number(m[2]) } : null
}

/* --------- LED palette ---------
   Solid + blink variant per kleur. mk2 heeft echte RGB;
   mk1 mapt onbekende kleuren naar dichtstbijzijnde uit {green,red,yellow}.
*/
const _solid = [
  ['off',      'Uit',        '#333'],
  ['white',    'Wit',        '#ffffff'],
  ['red',      'Rood',       '#ff3838'],
  ['orange',   'Oranje',     '#ff9500'],
  ['yellow',   'Geel',       '#ffcf40'],
  ['lime',     'Lime',       '#c8ff2b'],
  ['green',    'Groen',      '#3adf3a'],
  ['cyan',     'Cyaan',      '#00d9ff'],
  ['blue',     'Blauw',      '#337cff'],
  ['purple',   'Paars',      '#8b3fff'],
  ['magenta',  'Magenta',    '#ff2b8f'],
  ['pink',     'Roze',       '#ff7ac8'],
]
export const LED_COLORS = Object.fromEntries([
  ..._solid.map(([k, label, swatch]) => [k, { label, swatch }]),
  ..._solid.filter(([k]) => k !== 'off')
           .map(([k, label, swatch]) => [`${k}_blink`, { label: `${label} (knipper)`, swatch }]),
])
export const LED_COLOR_KEYS = Object.keys(LED_COLORS)

// APC Mini mk1: velocity 1-6 op ch 0 kiest kleur/knipper.
// Mapping van uitgebreide kleuren → dichtstbijzijnde mk1 primair.
const _mk1Buckets = {
  green:   ['green','lime','cyan'],
  red:     ['red','orange','blue','purple','magenta','pink'],
  yellow:  ['yellow','white'],
}
function _mk1Vel(color) {
  const solids = { green: 1, red: 3, yellow: 5 }
  const blinks = { green: 2, red: 4, yellow: 6 }
  const base = color.replace(/_blink$/,'')
  const bucket = Object.entries(_mk1Buckets).find(([, arr]) => arr.includes(base))?.[0] || 'red'
  return color.endsWith('_blink') ? blinks[bucket] : solids[bucket]
}
const _mk1Palette = { off: { ch: 0, vel: 0 } }
LED_COLOR_KEYS.forEach(k => { if (k !== 'off') _mk1Palette[k] = { ch: 0, vel: _mk1Vel(k) } })

// APC Mini mk2: channel = brightness/blink modus, velocity = palette-index.
// ch 6  = 100% solid, ch 14 = blink 1/4.
const _mk2Indices = {
  white: 1, red: 3, orange: 9, yellow: 13, lime: 17, green: 21,
  cyan: 33, blue: 45, purple: 49, magenta: 53, pink: 57,
}
const _mk2Palette = { off: { ch: 0, vel: 0 } }
for (const [name, idx] of Object.entries(_mk2Indices)) {
  _mk2Palette[name]           = { ch: 6,  vel: idx }
  _mk2Palette[`${name}_blink`] = { ch: 14, vel: idx }
}

export const DEVICE_MODELS = {
  mk1:  { label: 'APC Mini (mk1) — 3 kleuren', hasLeds: true, palette: _mk1Palette },
  mk2:  { label: 'APC Mini mk2 — RGB',         hasLeds: true, palette: _mk2Palette },
  lpd8: { label: 'AKAI LPD8 — 8 pads + 8 knobs (geen LEDs)', hasLeds: false, palette: {} },
}
export const DEVICE_MODEL_KEYS = Object.keys(DEVICE_MODELS)
function getModel(name) { return DEVICE_MODELS[name] || DEVICE_MODELS.mk2 }
export function modelHasLeds(name) { return !!getModel(name).hasLeds }

export function sendLED(output, key, colorName, modelName) {
  if (!output || !keyIsNote(key)) return
  const model = getModel(modelName || loadModel())
  if (!model.hasLeds) return
  const n = keyToNoteNum(key); if (!n) return
  const p = model.palette[colorName] || model.palette.off
  try { output.send([0x90 | (p.ch & 0x0F), n.num & 0x7F, p.vel & 0x7F]) } catch {}
}
export function clearAllLeds(output, modelName) {
  if (!output) return
  const model = getModel(modelName || loadModel())
  if (!model.hasLeds) return
  for (let n = 0; n <= 89; n++) {
    try { output.send([0x90, n & 0x7F, 0]) } catch {}
  }
}

/* --------- Normalized mappings --------- */
export function normalizeMapping(v) {
  if (typeof v === 'string') return { action: v, led_idle: 'off', led_active: 'off' }
  return {
    action:     v?.action || '',
    led_idle:   v?.led_idle   || 'off',
    led_active: v?.led_active || 'off',
  }
}

/* --------- Active-state evaluatie voor LED feedback --------- */
export function evalActive(action, ctx = {}) {
  if (!action) return false
  const state = ctx.state; const show = ctx.show
  const entries = (ctx.entries || []).filter(e => e.status === 'approved')

  if (action.startsWith('view.'))  return state?.current_view === action.slice(5)
  if (action.startsWith('phase.') && !['phase.next','phase.prev'].includes(action)) {
    return show?.phase === action.slice(6)
  }
  if (action.startsWith('entry.goto.')) {
    const n = Number(action.slice(11))
    return !!entries[n - 1] && state?.current_entry_id === entries[n - 1].id
  }
  if (action.startsWith('reveal.step.'))         return state?.reveal_step        === Number(action.slice(12))
  if (action.startsWith('public_reveal.step.'))  return state?.public_reveal_step === Number(action.slice(19))
  if (action === 'submissions.toggle')           return !!show?.submissions_open
  return false
}

export function applyLeds(output, mappings, ctx, modelName) {
  if (!output || !mappings) return
  const model = modelName || loadModel()
  for (const [key, raw] of Object.entries(mappings)) {
    if (!keyIsNote(key)) continue
    const m = normalizeMapping(raw)
    const on = evalActive(m.action, ctx)
    sendLED(output, key, on ? m.led_active : m.led_idle, model)
  }
}

/* --------- Persistence --------- */
const MAP_KEY     = 'imagecompo-midi-mappings'
const INPUT_KEY   = 'imagecompo-midi-input'
const OUTPUT_KEY  = 'imagecompo-midi-output'
const MODEL_KEY   = 'imagecompo-midi-model'
export function loadModel()      { try { return localStorage.getItem(MODEL_KEY) || 'mk2' } catch { return 'mk2' } }
export function saveModel(name)  { try { localStorage.setItem(MODEL_KEY, name || 'mk2') } catch {} }
export function loadMappings() {
  try {
    const raw = JSON.parse(localStorage.getItem(MAP_KEY) || '{}')
    const out = {}
    for (const [k, v] of Object.entries(raw)) out[k] = normalizeMapping(v)
    return out
  } catch { return {} }
}
export function saveMappings(m) { try { localStorage.setItem(MAP_KEY, JSON.stringify(m || {})) } catch {} }
export function loadInputId()   { try { return localStorage.getItem(INPUT_KEY)  || null } catch { return null } }
export function saveInputId(id) { try { id ? localStorage.setItem(INPUT_KEY, id)  : localStorage.removeItem(INPUT_KEY) } catch {} }
export function loadOutputId()  { try { return localStorage.getItem(OUTPUT_KEY) || null } catch { return null } }
export function saveOutputId(id){ try { id ? localStorage.setItem(OUTPUT_KEY, id) : localStorage.removeItem(OUTPUT_KEY) } catch {} }
